/**
 * Quantity - Represents quantity calculation data for pay items.
 * Used for computing quantities with factors, rounding, and overrides.
 */
export class Quantity {
  /** Calculation method identifier */
  Method: string;

  /** Description of the quantity calculation */
  Description: string;

  /** Factor item identifier */
  FactorItem: number;

  /** User-defined factor */
  FactorUser: number;

  /** Calculated or default factor */
  Factor: number;

  /** Override value */
  Override: number;

  /** Computed quantity */
  CompQuantity: number;

  /** Whether to use conservative rounding */
  RoundingConservative: boolean;

  /** Rounding increment value */
  RoundingIncrement: number;

  /** Rounding precision (decimal places) */
  RoundingPrecision: number;

  /** Final item quantity */
  ItemQuantity: number;

  /**
   * Creates a new Quantity instance
   * @param data - Object containing quantity properties
   */
  constructor(data: {
    Method: string;
    Description: string;
    FactorItem: number;
    FactorUser: number;
    Factor: number;
    Override: number;
    CompQuantity: number;
    RoundingConservative: boolean;
    RoundingIncrement: number;
    RoundingPrecision: number;
    ItemQuantity: number;
  }) {
    this.Method = data.Method;
    this.Description = data.Description;
    this.FactorItem = data.FactorItem;
    this.FactorUser = data.FactorUser;
    this.Factor = data.Factor;
    this.Override = data.Override;
    this.CompQuantity = data.CompQuantity;
    this.RoundingConservative = data.RoundingConservative;
    this.RoundingIncrement = data.RoundingIncrement;
    this.RoundingPrecision = data.RoundingPrecision;
    this.ItemQuantity = data.ItemQuantity;
  }

  /**
   * Creates a Quantity from a plain object (useful for deserialization)
   * @param obj - Plain object with quantity data
   * @returns New Quantity instance
   */
  static fromObject(obj: Partial<Quantity>): Quantity {
    return new Quantity({
      Method: obj.Method ?? "",
      Description: obj.Description ?? "",
      FactorItem: obj.FactorItem ?? 0,
      FactorUser: obj.FactorUser ?? 0,
      Factor: obj.Factor ?? 0,
      Override: obj.Override ?? 0,
      CompQuantity: obj.CompQuantity ?? 0,
      RoundingConservative: obj.RoundingConservative ?? false,
      RoundingIncrement: obj.RoundingIncrement ?? 0,
      RoundingPrecision: obj.RoundingPrecision ?? 0,
      ItemQuantity: obj.ItemQuantity ?? 0,
    });
  }

  /**
   * Converts the Quantity to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      Method: this.Method,
      Description: this.Description,
      FactorItem: this.FactorItem,
      FactorUser: this.FactorUser,
      Factor: this.Factor,
      Override: this.Override,
      CompQuantity: this.CompQuantity,
      RoundingConservative: this.RoundingConservative,
      RoundingIncrement: this.RoundingIncrement,
      RoundingPrecision: this.RoundingPrecision,
      ItemQuantity: this.ItemQuantity,
    };
  }
}


